import React, { useState } from 'react'

import { observer } from 'mobx-react-lite'
import Stack from '@mui/material/Stack'

import { useCombineEvents, useCombinePageviews, useWidgetLoadTimer } from '../common/hooks'
import { InsightsMicroWidgetDataProvider } from './context/InsightsMicroWidgetDataProvider'

import CarouselHeader from './carousel/CarouselHeader'
import MicroWidgetContainer from './MicroWidgetContainer'
import MicroBeatCarousel from './carousel/MicroBeatCarousel'
import { NoRelevantInsightsCard } from './cards/NoRelevantInsightsCard'
import { ZeroStateCard } from './cards/ZeroStateCard'
import Spinner from './Spinner'

import { AppStore } from './stores/AppStore'

import type { MicroWidgetProps } from './interfaces'
import { TRENDS_ANALYTICS_METADATA } from '../trends/constants/TrendsWidget'
import { SUPPORTED_CAROUSEL_HEIGHTS } from './constants/App'

const store = new AppStore()

/*
  This is the parent component / starting point for the general availabile insights micro widget. The Legacy/Cibc version is in a seperate repo
  called insights micro widget. The difference between the No relevent and zero state component is that No relevant is a zero state theat lets the
  user know that although there are no micro insights available but the user has fill insights available and zero state lets the user know that there
  aren't any micro or full insights available.
*/

const InsightsMicroWidget: React.FC<MicroWidgetProps> = ({
  allowSingleCardPeeking = true,
  areBeatsLoading = false,
  beats = [], // This also determines the length of the carousel controls (beat.length + 1)
  cardStyle = 'elevation 1',
  endpoint = '/',
  headerSx,
  heightOverrides,
  logOutUser = () => {},
  onCardClick = () => {},
  onEvent: onConsumerEvent = () => {},
  onPageview: onConsumerPageview = () => {},
  posthog,
  showCarouselControls,
  showHeader,
  showIcon,
  showWithMargin = true,
  token = '',
  userHasFullInsightFeedBeats,
  visibleCardsCount = 1,
  widthOverrides,
}) => {
  // ************** Derived Props ****************************
  const getResolvedVariant = () => {
    if (cardStyle === 'border') return 'outlined' as const
    if (cardStyle === 'elevation 1') return 'elevation' as const
    if (cardStyle === 'flat') return undefined
    return 'elevation'
  }
  const resolvedVariant = getResolvedVariant()
  const resolvedElevation = cardStyle === 'flat' ? 0 : 1

  // ************** Use States Hooks ***********************
  const [carouselSize, setCarouselSize] = useState<string>('STANDARD')
  const [isLoaded, setIsLoaded] = useState(false)
  const [triedInitializing, setTriedInitializing] = useState(false)

  // ************** Use Custom Hooks ***********************
  const onEvent = useCombineEvents([onConsumerEvent], store)
  const onPageview = useCombinePageviews([onConsumerPageview], store)

  useWidgetLoadTimer({
    widgetName: 'InsightsMicroWidget',
    isLoaded: isLoaded,
  })

  // ************** Use Effects Hooks **********************
  React.useEffect(() => {
    if (!store.isInitialized && !triedInitializing) {
      store.initialize(endpoint, token, logOutUser).catch(() => setTriedInitializing(false))
      setTriedInitializing(true)
    }
  }, [endpoint, token, logOutUser, store.isInitialized, triedInitializing])

  React.useEffect(() => {
    const pageHeight = window.innerHeight

    if (pageHeight < 180) {
      setCarouselSize('STANDARD')
    } else if (pageHeight >= 180 && pageHeight < 200) {
      setCarouselSize('LARGE')
    } else if (pageHeight >= 200 && pageHeight < 248) {
      setCarouselSize('XLARGE')
    } else if (pageHeight >= 248) {
      setCarouselSize('XXLARGE')
    }
  }, [window.innerHeight])

  React.useEffect(() => {
    setIsLoaded(!areBeatsLoading && store.isInitialized)
  }, [areBeatsLoading, store.isInitialized])

  // ************** Spreadable Props ***********************
  const generalProps = {
    heightOverrides,
    onCardClick,
    posthog,
    showIcon,
    showWithMargin,
    widthOverrides,
  }

  const zeroStateProps = {
    carouselSize,
    elevation: resolvedElevation,
    variant: resolvedVariant,
    heightOverrides,
    onCardClick,
    posthog,
    showIcon,
  }

  return (
    <InsightsMicroWidgetDataProvider onEvent={onEvent} onPageview={onPageview} store={store}>
      <MicroWidgetContainer heightOverrides={heightOverrides} widthOverrides={widthOverrides}>
        {!isLoaded && <Spinner />}

        {isLoaded && showHeader && (
          <CarouselHeader
            analyticsMetadata={TRENDS_ANALYTICS_METADATA}
            headerSx={headerSx}
            onCardClick={onCardClick}
            posthog={posthog}
            widthOverrides={widthOverrides}
          />
        )}

        {isLoaded && !!beats.length && (
          <MicroBeatCarousel
            allowSingleCardPeeking={allowSingleCardPeeking}
            analyticsMetadata={TRENDS_ANALYTICS_METADATA}
            beats={beats}
            elevation={resolvedElevation}
            showCarouselControls={showCarouselControls}
            variant={resolvedVariant}
            visibleCardsCount={visibleCardsCount}
            {...generalProps}
          />
        )}

        {isLoaded && !beats.length && (
          <Stack
            sx={{
              alignItems: 'center',
              height: heightOverrides,
              width: widthOverrides ? widthOverrides : '100%',
            }}
          >
            <Stack
              sx={{
                height: `calc(${SUPPORTED_CAROUSEL_HEIGHTS[carouselSize]}px - 44px)`,
                width: showWithMargin ? `calc(${widthOverrides}px - 32px)` : '100%',
              }}
            >
              {userHasFullInsightFeedBeats ? (
                <NoRelevantInsightsCard {...zeroStateProps} />
              ) : (
                <ZeroStateCard {...zeroStateProps} />
              )}
            </Stack>
          </Stack>
        )}
      </MicroWidgetContainer>
    </InsightsMicroWidgetDataProvider>
  )
}

export default observer(InsightsMicroWidget)
